import { apiStatic } from 'AurionCR/components';
import { notifyRequestResult } from 'AurionCR/store/modules/notify';
import { queryEncoderObject } from 'components/helpers';
import { startOfDay } from 'date-fns';
import {
  call,
  cancel,
  cancelled,
  delay,
  fork,
  put,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';
import {
  API_RATING_MATRIX_DOCUMENT,
  eRatingMatrixDocumentSteps,
} from 'services/rating-matrix-document';
import { PAGE_SIZE } from '.';
import {
  checkStatusesFailure,
  checkStatusesSuccess,
  DOCUMENT_PREPARATION_ACTIONS,
  loadPageSuccess,
  startPreparationFailure,
  startPreparationSuccess,
  updateGenerateBtnState,
} from './types';

// Helper function to get latest records
const getData = async (page: number = 1) => {
  return apiStatic.post(
    `${API_RATING_MATRIX_DOCUMENT.GET_ALL_DYNAMIC}`,
    queryEncoderObject({
      Select:
        'id,title,createdDate,step,ratingPerformanceCategoryID,exportDate,ratingsExcel,outlooksExcel,sectorExcel',
      OrderBy: 'createdDate desc',
      Take: PAGE_SIZE,
      Skip: (page - 1) * PAGE_SIZE,
      Count: true,
    }),
  );
};

// Helper function to check if time has expired (30 minutes)
const isTimeExpired = (lastRecordDate: string): boolean => {
  const lastDate = new Date(lastRecordDate + 'Z');
  const currentDate = new Date();
  const timeDiff = currentDate.getTime() - lastDate.getTime();
  const thirtyMinutes = 30 * 60 * 1000; // 30 min in ms
  return timeDiff > thirtyMinutes;
};

// Helper function to determine if process should continue
const shouldContinueMonitoring = (records: any[]): boolean => {
  if (records.length === 0) return false;

  const lastRecord = records[0];

  // Stop if time has expired
  if (isTimeExpired(lastRecord.createdDate)) {
    return false;
  }

  // Continue if there are records with incomplete status
  return records.some((record) => record.step !== eRatingMatrixDocumentSteps.Done);
};

// Helper function to determine if generate button should be disabled
const shouldDisableGenerateBtn = (records: any[]): boolean => {
  if (records.length === 0) return false;
  const lastRecord = records[0];
  if (!lastRecord?.createdDate) return false;
  return startOfDay(new Date(lastRecord.createdDate + 'Z')) >= startOfDay(new Date());
};

// Saga for starting document preparation
function* startDocumentPreparationSaga(): Generator<any, void, any> {
  try {
    // Make API request to start document preparation
    yield call(apiStatic.post, API_RATING_MATRIX_DOCUMENT.EXPORT_RATING_MATRIX);

    // Wait 1 second to allow the API to process the request
    yield delay(1000);

    // Start monitoring - this will trigger the first status check
    yield put({ type: DOCUMENT_PREPARATION_ACTIONS.START_MONITORING });

    yield put(startPreparationSuccess());
  } catch (error: any) {
    yield call(
      notifyRequestResult,
      error.message || 'Failed to start document preparation',
      'error',
    );
    yield put(startPreparationFailure(error.message || 'Failed to start document preparation'));
  }
}

// Saga for checking statuses
function* checkStatusesSaga(): Generator<any, void, any> {
  try {
    const response = yield call(getData);
    const records = response.data.value;
    const totalPages = Math.ceil(response.data.count / PAGE_SIZE);

    yield put(checkStatusesSuccess(records, totalPages));
    yield put(updateGenerateBtnState(shouldDisableGenerateBtn(records)));

    // Check if we should continue monitoring, but don't fork monitoringSaga here
    // The monitoringSaga is already running from startMonitoringSaga
    if (!shouldContinueMonitoring(records)) {
      // Stop monitoring if process is completed or time expired
      yield put({ type: DOCUMENT_PREPARATION_ACTIONS.STOP_MONITORING });
    }
  } catch (error: any) {
    yield call(notifyRequestResult, error.message || 'Failed to check statuses', 'error');
    yield put(checkStatusesFailure(error.message || 'Failed to check statuses'));
  }
}

// Saga for loading specific page
function* loadPageSaga(action: any): Generator<any, void, any> {
  try {
    const page = action.payload;
    const response = yield call(getData, page);
    const records = response.data.value;
    const totalPages = Math.ceil(response.data.count / PAGE_SIZE);

    yield put(loadPageSuccess(records, totalPages));
  } catch (error: any) {
    yield call(notifyRequestResult, error.message || 'Failed to load page', 'error');
    yield put(checkStatusesFailure(error.message || 'Failed to load page'));
  }
}

// Saga for periodic monitoring
function* monitoringSaga(): Generator<any, void, any> {
  try {
    while (true) {
      // Wait 1 minutes
      yield delay(1 * 60 * 1000);

      // Check if we should continue monitoring
      const state = yield select((state: any) => state.documentPreparationReducer);

      // Stop if reports are not being created
      if (!state.isCreatingReports) {
        break;
      }

      // Check statuses
      yield put({ type: DOCUMENT_PREPARATION_ACTIONS.CHECK_STATUSES });
    }
  } finally {
    if (yield cancelled()) {
      // Cleanup if saga is cancelled
      yield put({ type: DOCUMENT_PREPARATION_ACTIONS.STOP_MONITORING });
    }
  }
}

function* startMonitoringSaga(): Generator<any, void, any> {
  try {
    // Start first status check immediately after starting monitoring
    yield put({ type: DOCUMENT_PREPARATION_ACTIONS.CHECK_STATUSES });

    const monitoringTask = yield fork(monitoringSaga);

    // Wait for stop monitoring signal
    yield take(DOCUMENT_PREPARATION_ACTIONS.STOP_MONITORING);

    // Cancel the monitoring task
    yield cancel(monitoringTask);
  } catch (error: any) {
    console.error('Error in startMonitoringSaga:', error);
  }
}

// Saga for initializing monitoring
function* initMonitoringSaga(): Generator<any, void, any> {
  try {
    // Check if monitoring is already initialized BEFORE dispatching INIT_MONITORING
    const state = yield select((state: any) => state.documentPreparationReducer);

    if (!state.isInit) {
      // Start monitoring only if not already initialized
      yield put({ type: DOCUMENT_PREPARATION_ACTIONS.START_MONITORING });
    }
    // Note: isInit will be set to true by the reducer after this saga completes
  } catch (error: any) {
    console.error('Failed to initialize monitoring:', error);
  }
}

// Main saga watcher
export const documentPreparationSaga = [
  takeEvery(DOCUMENT_PREPARATION_ACTIONS.START_PREPARATION, startDocumentPreparationSaga),
  takeEvery(DOCUMENT_PREPARATION_ACTIONS.CHECK_STATUSES, checkStatusesSaga),
  takeEvery(DOCUMENT_PREPARATION_ACTIONS.LOAD_PAGE, loadPageSaga),
  takeEvery(DOCUMENT_PREPARATION_ACTIONS.INIT_MONITORING, initMonitoringSaga),
  takeEvery(DOCUMENT_PREPARATION_ACTIONS.START_MONITORING, startMonitoringSaga),
];
